import { createContext, useContext, useEffect, useState } from 'react'
import { getPublicKey, nip19 } from 'nostr-tools'
import { ADMIN_PUBKEYS } from '../config/constants'
import { seedPhraseManager } from '../lib/seedPhraseManager'

export type LoginMethod = 'extension' | 'seed' | 'read_only' | null;

interface AuthContextType {
  pubkey: string | null
  sk: Uint8Array | null
  loginMethod: LoginMethod
  loading: boolean
  login: () => Promise<void>
  loginWithSeed: (mnemonic: string) => void
  loginWithKey: (key: string) => void
  logout: () => void
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [pubkey, setPubkey] = useState<string | null>(null)
  const [sk, setSk] = useState<Uint8Array | null>(null)
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(null)
  const [loading, setLoading] = useState(true)

  // Hex to Uint8Array helper
  const hexToBytes = (hex: string) => {
    return new Uint8Array(hex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)))
  }

  // Uint8Array to Hex helper
  const bytesToHex = (bytes: Uint8Array) => {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  const login = async () => {
    try {
      if (!window.nostr) {
        throw new Error('Nostr uzantısı bulunamadı.');
      }
      const pk = await window.nostr.getPublicKey();
      setPubkey(pk);
      setSk(null);
      setLoginMethod('extension');
      localStorage.setItem('nostr_pubkey', pk);
      localStorage.setItem('nostr_login_method', 'extension');
      localStorage.removeItem('nostr_sk');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
      console.error('Nostr login error:', errorMessage);
      throw error instanceof Error ? error : new Error(errorMessage);
    }
  };

  const loginWithSeed = (mnemonic: string) => {
    if (!seedPhraseManager.validateMnemonic(mnemonic)) {
      throw new Error('Geçersiz 12 kelime.');
    }
    const derivedSk = seedPhraseManager.deriveSecretKey(mnemonic);
    const derivedPk = getPublicKey(derivedSk);

    setSk(derivedSk);
    setPubkey(derivedPk);
    setLoginMethod('seed');

    localStorage.setItem('nostr_pubkey', derivedPk);
    localStorage.setItem('nostr_sk', bytesToHex(derivedSk));
    localStorage.setItem('nostr_login_method', 'seed');
  };

  const loginWithKey = (key: string) => {
    try {
      const trimmed = key.trim();
      // Handle nsec / npub
      if (trimmed.startsWith('nsec') || trimmed.startsWith('npub')) {
        const { type, data } = nip19.decode(trimmed);
        if (type === 'nsec') {
          const secretKey = data as Uint8Array;
          const publicKey = getPublicKey(secretKey);
          setSk(secretKey);
          setPubkey(publicKey);
          setLoginMethod('seed'); // We treat imported nsec as 'seed' since we have SK
          localStorage.setItem('nostr_pubkey', publicKey);
          localStorage.setItem('nostr_sk', bytesToHex(secretKey));
          localStorage.setItem('nostr_login_method', 'seed');
        } else if (type === 'npub') {
          const publicKey = data as string;
          setPubkey(publicKey);
          setSk(null);
          setLoginMethod('read_only');
          localStorage.setItem('nostr_pubkey', publicKey);
          localStorage.setItem('nostr_login_method', 'read_only');
          localStorage.removeItem('nostr_sk');
        }
      } else {
        // Assume hex
        if (trimmed.length === 64) {
          const secretKey = hexToBytes(trimmed);
          const publicKey = getPublicKey(secretKey);
          setSk(secretKey);
          setPubkey(publicKey);
          setLoginMethod('seed');
          localStorage.setItem('nostr_pubkey', publicKey);
          localStorage.setItem('nostr_sk', bytesToHex(secretKey));
          localStorage.setItem('nostr_login_method', 'seed');
        } else {
          throw new Error('Geçersiz anahtar formatı.');
        }
      }
    } catch (error) {
      throw new Error('Giriş başarısız: Geçersiz anahtar.');
    }
  };

  const logout = () => {
    setPubkey(null);
    setSk(null);
    setLoginMethod(null);
    localStorage.removeItem('nostr_pubkey');
    localStorage.removeItem('nostr_sk');
    localStorage.removeItem('nostr_login_method');
  };

  useEffect(() => {
    const savedPubkey = localStorage.getItem('nostr_pubkey');
    const savedSk = localStorage.getItem('nostr_sk');
    const savedMethod = localStorage.getItem('nostr_login_method') as LoginMethod;

    if (savedSk) {
      const recoveredSk = hexToBytes(savedSk);
      setSk(recoveredSk);
      setPubkey(getPublicKey(recoveredSk));
      setLoginMethod(savedMethod || 'seed');
    } else if (savedPubkey) {
      setPubkey(savedPubkey);
      setLoginMethod(savedMethod || 'read_only');
    }
    setLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        pubkey,
        sk,
        loginMethod,
        loading,
        login,
        loginWithSeed,
        loginWithKey,
        logout,
        isAdmin: pubkey ? ADMIN_PUBKEYS.includes(pubkey) : false,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

declare global {
  interface Window {
    nostr?: {
      getPublicKey: () => Promise<string>;
      signEvent: (event: any) => Promise<any>;
      nip04?: {
        encrypt: (pubkey: string, plaintext: string) => Promise<string>;
        decrypt: (pubkey: string, ciphertext: string) => Promise<string>;
      };
      nip44?: {
        encrypt: (pubkey: string, plaintext: string) => Promise<string>;
        decrypt: (pubkey: string, ciphertext: string) => Promise<string>;
      };
    };
  }
}
